import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { CardComponent } from '@custom-components/general/card/card.component';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { TableComponent } from '@custom-components/general/table/table.component';
import { NoItemsFoundComponent } from '@custom-components/general/no-items-found/no-items-found.component';
import { ListRowComponent } from '@custom-components/general/list-row';
import { TagComponent } from '@custom-components/general/tag/tag.component';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { TagStateOf, alarmConfigGroupLabelKey } from '@core/helpers';
import { ALARM_ADMIN_ROLES } from '../../constants/alarm-admin-roles.constant';
import {
  AlarmConfigResponseModel,
  PartnerUserModel,
  PartnerUsersResponseModel,
  capitalizePriority,
  getUserActivePartner,
  lowercasePriority,
} from '@core/models';
import { AlarmConfigService } from '@core/services/alarm-config.service';
import { CompanyService } from '@core/services/company.service';
import { UserStore } from '@root/app/store/user.store';
import {
  DeviceOverridesDialogComponent,
  DeviceOverridesDialogData,
  toDeviceOverrideRow,
} from '../device-overrides-dialog/device-overrides-dialog.component';
import {
  AddRecipientsDialogComponent,
  AddRecipientsDialogData,
} from '../add-recipients-dialog/add-recipients-dialog.component';
import { RecipientCandidate } from '../../mock/alarm-recipient-candidates.mock';

type AlarmSettingsPriority = 'High' | 'Medium' | 'Low';

interface AlarmTypeSettingsRow extends Record<string, unknown> {
  id: string;
  label: string;
  priority: AlarmSettingsPriority;
  priorityState: ReturnType<typeof TagStateOf.alarmPriority>;
  showInPortal: boolean;
  emailNotification: boolean;
  deviceOverrideCount: number;
  /** Straight from the API's `configurable` — false for config groups whose priority and
   * show_in_portal/email_enabled settings are Xemex-fixed (the sim.* groups). Renders all three
   * cells read-only and suppresses the "manage overrides" action, which the backend rejects with
   * a 403 for these groups. */
  configurable: boolean;
}

interface RecipientRow {
  id: string;
  /** Null when `id` resolves to no partner user; the template then labels the row. */
  name: string | null;
  email: string;
  /** Whether `id` currently resolves to a user in the active partner's own user list —
   * false for stale recipients (e.g. a user removed from the company after being added). */
  valid: boolean;
}

const ACTIVE_USER_STATUS = 'Active';
const PARTNER_USERS_PAGE_LIMIT = 500;

@Component({
  selector: 'xemex-alarms-settings-tab',
  imports: [
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    CardComponent,
    FilledButtonComponent,
    TextButtonComponent,
    TableComponent,
    NoItemsFoundComponent,
    ListRowComponent,
    TagComponent,
  ],
  templateUrl: './alarms-settings-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AlarmsSettingsTabComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly alarmConfigService = inject(AlarmConfigService);
  private readonly companyService = inject(CompanyService);
  private readonly userStore = inject(UserStore);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);

  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  /** True when GET /alarm-config itself failed (e.g. the viewer's group lacks the backend
   * `manage_alarm_settings` feature grant) — distinct from a genuinely empty config, so the
   * page can say why it's blank instead of silently showing zero alarm types. */
  protected readonly loadError = signal(false);

  /** Draft state shown/edited in the UI. Toggles and recipient add/remove only touch these —
   * nothing is sent to the backend until `saveChanges()` runs. */
  protected readonly alarmTypes = signal<AlarmTypeSettingsRow[]>([]);
  protected readonly recipients = signal<RecipientRow[]>([]);

  /** Last-known-persisted state, used to detect unsaved changes and to revert on cancel/error.
   * Must be signals, not plain fields — `hasUnsavedChanges` is a `computed()` that only
   * re-evaluates when a signal it reads changes; mutating a plain field after save wouldn't
   * trigger a recompute and the banner would never clear. */
  private readonly savedAlarmTypes = signal<AlarmTypeSettingsRow[]>([]);
  private readonly savedRecipients = signal<RecipientRow[]>([]);

  /** Active partner users, keyed by objectId — the candidate pool for "Add recipient" and
   * the display source for already-added recipients. */
  private readonly partnerUsersById = signal<Map<string, PartnerUserModel>>(new Map());

  /** True only when the users fetch succeeded and returned every user. Only then does a
   * recipient missing from the list mean an unknown user; otherwise the row keeps its oid. */
  protected readonly partnerUsersComplete = signal(false);

  protected readonly emailEnabledCount = computed(
    () => this.alarmTypes().filter(r => r.emailNotification).length,
  );

  protected readonly hasUnsavedChanges = computed(() => {
    const saved = this.savedAlarmTypes();
    const typesChanged = this.alarmTypes().some((row, i) => {
      const savedRow = saved[i];
      return (
        !savedRow ||
        savedRow.showInPortal !== row.showInPortal ||
        savedRow.emailNotification !== row.emailNotification ||
        savedRow.priority !== row.priority
      );
    });
    if (typesChanged) return true;

    const currentIds = this.recipients().map(r => r.id);
    const savedIds = this.savedRecipients().map(r => r.id);
    return currentIds.length !== savedIds.length || currentIds.some(id => !savedIds.includes(id));
  });

  /** True when the recipient list contains a user who is not (or no longer) part of the
   * active partner's own user list — saving is blocked while this holds. */
  protected readonly hasInvalidRecipients = computed(() => this.recipients().some(r => !r.valid));

  protected readonly columns: TableColumnConfig[] = [
    {
      field: 'label',
      label: 'alarmsPage.settings.alarmTypes.columns.type',
      type: TableColumnType.Text,
      bold: true,
      width: '30%',
    },
    {
      field: 'priority',
      label: 'alarmsPage.settings.alarmTypes.columns.priority',
      type: TableColumnType.Select,
      stateField: 'priorityState',
      width: '15%',
      selectOptions: [
        { value: 'High', label: 'High', state: TagStateOf.alarmPriority('High') },
        { value: 'Medium', label: 'Medium', state: TagStateOf.alarmPriority('Medium') },
        { value: 'Low', label: 'Low', state: TagStateOf.alarmPriority('Low') },
      ],
      // The medium/high split is the alarm's identity for a locked group (two separate backend
      // rule tiers raise the two config groups), so the dropdown renders as a plain tag there.
      disabledWhen: row => row['configurable'] === false,
    },
    {
      field: 'showInPortal',
      label: 'alarmsPage.settings.alarmTypes.columns.showInPortal',
      type: TableColumnType.Toggle,
      width: '20%',
      disabledWhen: row => row['configurable'] === false,
    },
    {
      field: 'emailNotification',
      label: 'alarmsPage.settings.alarmTypes.columns.email',
      type: TableColumnType.Toggle,
      width: '20%',
      disabledWhen: row => row['configurable'] === false,
    },
  ];

  ngOnInit(): void {
    this.loadAlarmConfig();
  }

  private get partnerOid(): string | undefined {
    return getUserActivePartner(this.userStore.user())?.partnerOid;
  }

  private loadAlarmConfig(): void {
    const partnerOid = this.partnerOid;
    if (!partnerOid) return;

    this.loading.set(true);
    this.loadError.set(false);
    forkJoin({
      alarmConfig: this.alarmConfigService.getAlarmConfig().pipe(catchError(() => of(null))),
      partnerUsers: this.companyService
        .getUsersByPartner(partnerOid, 0, PARTNER_USERS_PAGE_LIMIT)
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ alarmConfig, partnerUsers }) => {
        this.loading.set(false);

        const usersById = new Map<string, PartnerUserModel>();
        // getUsersByPartner's declared Observable<PartnerUsersResponseModel[]> return type is
        // misleading — the API actually returns a single object (see usage in
        // my-company-users-tab.component.ts: `usersByMyCompany().users`).
        const usersResponse = partnerUsers as unknown as PartnerUsersResponseModel | null;
        const users = usersResponse?.users ?? [];
        users.forEach(u => usersById.set(u.objectId, u));
        this.partnerUsersComplete.set(
          !!usersResponse && users.length >= (usersResponse.numberOfItems ?? users.length),
        );

        // The viewer is always a valid recipient candidate if their role can manage alarm
        // settings at all (same role set as the Settings-tab gate in alarms-page.component.ts) —
        // don't depend on the users fetch having actually returned their own record, since it may
        // fail or be paginated out.
        const currentUser = this.userStore.user();
        const activePartnerRoleName = getUserActivePartner(currentUser)?.roles[0]?.roleName;
        if (
          currentUser &&
          activePartnerRoleName &&
          ALARM_ADMIN_ROLES.includes(activePartnerRoleName)
        ) {
          usersById.set(currentUser.object_id, {
            objectId: currentUser.object_id,
            fullName: currentUser.fullName,
            email: currentUser.email,
            status: ACTIVE_USER_STATUS,
            createdDate: '',
            roleId: 0,
            roleName: activePartnerRoleName,
            ...usersById.get(currentUser.object_id),
          });
        }
        this.partnerUsersById.set(usersById);

        if (!alarmConfig) {
          this.loadError.set(true);
          return;
        }
        const types = alarmConfig.items.map(item => this.toRow(item));
        // Recipients are a single global list (Stijn/Ids, 2026-07-10) even though the store keeps
        // them per config-group — union across all groups rather than trusting items[0], in case
        // groups have drifted out of sync (e.g. seeded data, partial updates outside this UI).
        const recipientIds = [...new Set(alarmConfig.items.flatMap(item => item.recipients))];
        // Nobody has configured recipients yet — default to every partner admin of the
        // viewing partner rather than showing an empty list, so alarm emails don't silently
        // go nowhere. Treated as already "saved" so the unsaved-changes banner doesn't fire the
        // moment the tab opens — it should only appear once the user actually changes something
        // (recipients or alarm-type settings), not merely because defaults are being displayed.
        const recipientRows = recipientIds.length
          ? this.toRecipientRows(recipientIds, usersById)
          : this.defaultPartnerAdminRows(usersById);
        this.alarmTypes.set(types);
        this.recipients.set(recipientRows);
        this.savedAlarmTypes.set(types);
        this.savedRecipients.set(recipientRows);
      });
  }

  private toRow(item: AlarmConfigResponseModel): AlarmTypeSettingsRow {
    const priority = capitalizePriority(item.priority);
    return {
      id: item.config_group,
      label: this.labelFor(item.config_group),
      priority,
      priorityState: TagStateOf.alarmPriority(priority),
      showInPortal: item.show_in_portal,
      emailNotification: item.email_enabled,
      deviceOverrideCount: item.device_override_count,
      configurable: item.configurable,
    };
  }

  private labelFor(configGroup: string): string {
    return this.translateService.instant(alarmConfigGroupLabelKey(configGroup));
  }

  private toRecipientRows(ids: string[], usersById: Map<string, PartnerUserModel>): RecipientRow[] {
    return ids.map(id => {
      const user = usersById.get(id);
      return { id, name: user?.fullName ?? null, email: user?.email ?? '', valid: !!user };
    });
  }

  private defaultPartnerAdminRows(usersById: Map<string, PartnerUserModel>): RecipientRow[] {
    return Array.from(usersById.values())
      .filter(u => u.status === ACTIVE_USER_STATUS && ALARM_ADMIN_ROLES.includes(u.roleName))
      .map(u => ({ id: u.objectId, name: u.fullName, email: u.email, valid: true }));
  }

  protected onToggleChange({ row, field }: { row: AlarmTypeSettingsRow; field: string }): void {
    if (field === 'showInPortal') {
      this.updateRow(row, { showInPortal: !row.showInPortal });
    } else if (field === 'emailNotification') {
      this.updateRow(row, { emailNotification: !row.emailNotification });
    }
  }

  protected onSelectChange({
    row,
    field,
    value,
  }: {
    row: AlarmTypeSettingsRow;
    field: string;
    value: string;
  }): void {
    if (field !== 'priority') return;
    const priority = value as AlarmSettingsPriority;
    this.updateRow(row, { priority, priorityState: TagStateOf.alarmPriority(priority) });
  }

  /** Local-only — nothing is persisted until `saveChanges()` runs. */
  private updateRow(row: AlarmTypeSettingsRow, changes: Partial<AlarmTypeSettingsRow>): void {
    const updated: AlarmTypeSettingsRow = { ...row, ...changes };
    this.alarmTypes.update(rows => rows.map(r => (r.id === row.id ? updated : r)));
  }

  /** Local-only — nothing is persisted until `saveChanges()` runs. */
  protected removeRecipient(row: RecipientRow): void {
    this.recipients.update(rows => rows.filter(r => r.id !== row.id));
  }

  protected openDeviceOverrides(row: AlarmTypeSettingsRow): void {
    this.alarmConfigService
      .getDeviceOverrides(row.id)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(collection => {
        if (!collection) {
          this.snackBar.open(
            this.translateService.instant('alarmsPage.settings.alarmTypes.loadOverridesError'),
            '',
            { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
          );
          return;
        }
        this.dialog
          .open(DeviceOverridesDialogComponent, {
            width: '58rem',
            maxWidth: 'calc(100vw - 2rem)',
            data: {
              configGroup: row.id,
              alarmTypeLabel: row.label,
              globalShowInPortal: row.showInPortal,
              globalEmailNotification: row.emailNotification,
              devices: collection.items.map(item => toDeviceOverrideRow(item)),
            } satisfies DeviceOverridesDialogData,
          })
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            // loadAlarmConfig() resets alarmTypes/recipients (and their saved counterparts)
            // from the backend, which would silently discard any staged-but-unsaved edits —
            // only refresh deviceOverrideCount for this row when nothing else is staged.
            if (this.hasUnsavedChanges()) return;
            this.loadAlarmConfig();
          });
      });
  }

  protected openAddRecipients(): void {
    const usersById = this.partnerUsersById();
    const excludeIds = this.recipients().map(r => r.id);
    const candidates: RecipientCandidate[] = Array.from(usersById.values())
      .filter(u => u.status === ACTIVE_USER_STATUS)
      .map(u => ({ id: u.objectId, name: u.fullName, email: u.email, role: u.roleName }));

    this.dialog
      .open(AddRecipientsDialogComponent, {
        width: '32rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: { candidates, excludeIds } satisfies AddRecipientsDialogData,
      })
      .afterClosed()
      .pipe(filter((selected): selected is RecipientCandidate[] => !!selected?.length))
      .subscribe(selected => {
        // Local-only — nothing is persisted until `saveChanges()` runs. Candidates always come
        // from the active partner's own user list (see openAddRecipients above), so they're valid.
        this.recipients.update(previous => [
          ...previous,
          ...selected.map(c => ({ id: c.id, name: c.name, email: c.email, valid: true })),
        ]);
      });
  }

  /** Persists every staged edit (alarm-type toggles and the recipients list) in one go, only
   * once the user explicitly confirms via "Wijzigingen opslaan". Recipients are a single global
   * list (Stijn/Ids, 2026-07-10) even though the store keeps them per config-group — write the
   * new list to every group, keeping each group's own show_in_portal/email_enabled untouched. */
  protected saveChanges(): void {
    if (this.hasInvalidRecipients()) {
      this.snackBar.open(
        this.translateService.instant(
          'alarmsPage.settings.unsavedChanges.saveErrorInvalidRecipients',
        ),
        '',
        { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
      );
      return;
    }

    const rows = this.alarmTypes();
    const savedRows = this.savedAlarmTypes();
    const recipientIds = this.recipients().map(r => r.id);
    const savedRecipientIds = this.savedRecipients().map(r => r.id);
    // Recipients are a single global list written onto every group (see class comment above),
    // so a recipients-only edit still needs to reach every row. Otherwise only rows whose own
    // settings changed should fire a request — looping over every row unconditionally used to
    // send one PUT per alarm type even when only a single toggle changed.
    const recipientsChanged =
      recipientIds.length !== savedRecipientIds.length ||
      recipientIds.some(id => !savedRecipientIds.includes(id));
    const changedRows = rows.filter(row => {
      const savedRow = savedRows.find(r => r.id === row.id);
      if (!savedRow) return true;
      return (
        recipientsChanged ||
        savedRow.showInPortal !== row.showInPortal ||
        savedRow.emailNotification !== row.emailNotification ||
        savedRow.priority !== row.priority
      );
    });

    if (!changedRows.length) {
      this.saving.set(false);
      return;
    }

    this.saving.set(true);

    forkJoin(
      changedRows.map(row => {
        const savedRow = savedRows.find(r => r.id === row.id);
        return this.alarmConfigService
          .updateAlarmConfig(row.id, {
            show_in_portal: row.showInPortal,
            email_enabled: row.emailNotification,
            priority: lowercasePriority(row.priority),
            recipients: recipientIds,
          })
          .pipe(
            switchMap(result =>
              this.cascadeDeviceOverrides(row, savedRow).pipe(
                map(() => ({ ok: true as const, result })),
              ),
            ),
            // ApiService.formatErrors unwraps HttpErrorResponse to its parsed body (see
            // data-export-page.component.ts:723), so `instanceof HttpErrorResponse` never
            // matches here — use a plain ok/error marker instead of relying on error shape.
            catchError(error => of({ ok: false as const, error })),
          );
      }),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.saving.set(false);
        const failures = results.filter(r => !r.ok);
        if (failures.length) {
          // eslint-disable-next-line no-console -- surface the real cause; the toast below is generic on purpose
          console.error('Alarm config save failed', failures);
          const status = (failures[0] as { error?: { status?: number } }).error?.status;
          const messageKey =
            status === 401 || status === 403
              ? 'alarmsPage.settings.unsavedChanges.saveErrorForbidden'
              : 'alarmsPage.settings.unsavedChanges.saveError';
          this.snackBar.open(this.translateService.instant(messageKey), '', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
          });
          return;
        }
        this.savedRecipients.set(this.recipients());
        this.loadAlarmConfig();
        this.snackBar.open(
          this.translateService.instant('alarmsPage.settings.unsavedChanges.saveSuccess'),
          '',
          { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
        );
      });
  }

  /** Changing an alarm type's global Show in portal/Email notification must also reach every
   * device that currently has its own override on that field — otherwise the device keeps
   * showing/emailing per its stale override while the alarm type's own row says otherwise.
   * Clears (sets to `null`) the overridden field so the device falls back to the new global
   * value, exactly like toggling the same global switch inside the device-overrides dialog. */
  private cascadeDeviceOverrides(
    row: AlarmTypeSettingsRow,
    savedRow: AlarmTypeSettingsRow | undefined,
  ) {
    // A locked group has no device overrides to cascade to (the backend 403s both the read and the
    // write), and its own toggles can't have changed - it only reaches saveChanges at all because a
    // recipients edit fans out across every group.
    if (!savedRow || !row.configurable) return of(null);
    const showInPortalChanged = savedRow.showInPortal !== row.showInPortal;
    const emailChanged = savedRow.emailNotification !== row.emailNotification;
    if (!showInPortalChanged && !emailChanged) return of(null);

    return this.alarmConfigService.getDeviceOverrides(row.id).pipe(
      switchMap(collection => {
        const updates = collection.items
          .filter(
            item =>
              (showInPortalChanged && item.show_in_portal !== null) ||
              (emailChanged && item.email_enabled !== null),
          )
          .map(item =>
            this.alarmConfigService.updateDeviceOverride(row.id, item.device_id, {
              show_in_portal: showInPortalChanged ? null : item.show_in_portal,
              email_enabled: emailChanged ? null : item.email_enabled,
            }),
          );
        return updates.length ? forkJoin(updates) : of(null);
      }),
      catchError(() => of(null)),
    );
  }

  protected discardChanges(): void {
    this.alarmTypes.set(this.savedAlarmTypes());
    this.recipients.set(this.savedRecipients());
  }
}
